import { type ReactElement } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { type CalloutReleaseView } from '@marketplace/contracts';
import { formatMoney } from '../checkout/money.js';
import { RELEASE_WATCH_EVERY_MS, RELEASE_WATCH_FOR_MS } from './callout-release.js';
import { useWatch } from './watch.js';

export interface CalloutReleaseStatusProps {
  /** What `loadCalloutRelease` answered; `null` when it could not be read. */
  release: CalloutReleaseView | null;
  locale: string;
}

/**
 * `W5-T18` §4.8 — under *"The client approved."*, where the professional's share of the call-out fee
 * is: on its way, waiting for Stripe (with the way to fix it), or sent.
 *
 * The amount is the server's integer cents, formatted, never recomputed. While the transfer is being
 * sent the route's loaders are re-run every three seconds for a minute (`useWatch`), which is how the
 * line turns to *sent* without a reload. `NOT_STARTED`, a failed read and a share of 0 (the platform
 * kept the whole fee, AC 3) show nothing, so the approval line stands alone.
 */
export function CalloutReleaseStatus({
  release,
  locale,
}: CalloutReleaseStatusProps): ReactElement | null {
  const { t } = useTranslation();
  useWatch(release?.state === 'SENDING', {
    everyMs: RELEASE_WATCH_EVERY_MS,
    forMs: RELEASE_WATCH_FOR_MS,
  });

  // Nothing started carries no amount; nothing owed (the platform kept the whole fee) says nothing.
  if (release?.providerShare === undefined || release.providerShare.amountCents === 0) return null;
  const amount = formatMoney(release.providerShare, locale);
  const sentOn =
    release.sentAt === undefined
      ? null
      : new Intl.DateTimeFormat(locale, { dateStyle: 'long' }).format(new Date(release.sentAt));
  if (release.state === 'SENT' && sentOn === null) return null;

  return (
    <section className="mp-section" data-testid="callout-release" aria-live="polite">
      {release.state === 'SENDING' ? (
        <p>{t('release.pro.sending', { amount })}</p>
      ) : release.state === 'WAITING_FOR_STRIPE' ? (
        <>
          <p>{t('release.pro.waiting', { amount })}</p>
          <p>
            <Link to={`/${locale}/account/payouts`}>{t('release.pro.finishPayouts')}</Link>
          </p>
        </>
      ) : (
        <p>{t('release.pro.sent', { amount, date: sentOn })}</p>
      )}
    </section>
  );
}
